import { useAuth } from "@/contexts/AuthContext";
import { useGame, BUILDING_COIN_INCOME, BuildingType } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

const BUILDING_LABELS: Record<BuildingType, string> = {
  house: "住宅",
  company: "企業",
  powerPlant: "発電所",
};

const BUILDING_ICONS: Record<BuildingType, string> = {
  house: "ri-home-4-line",
  company: "ri-building-4-line",
  powerPlant: "ri-flashlight-line",
};

const BUILDING_COLORS: Record<BuildingType, { bar: string; text: string; bg: string }> = {
  house: { bar: "bg-emerald-400", text: "text-emerald-400", bg: "bg-emerald-400/10" },
  company: { bar: "bg-sky-400", text: "text-sky-400", bg: "bg-sky-400/10" },
  powerPlant: { bar: "bg-yellow-400", text: "text-yellow-400", bg: "bg-yellow-400/10" },
};

export default function IncomeChart() {
  const { user } = useAuth();
  const { blocks } = useGame();
  const { isDark } = useTheme();

  const myBlocks = user
    ? Object.values(blocks).filter((b) => b.ownerId === user.id)
    : [];

  const buildingCounts: Record<BuildingType, number> = {
    house: 0,
    company: 0,
    powerPlant: 0,
  };

  myBlocks.forEach((b) => {
    b.buildings.forEach((bld) => {
      buildingCounts[bld.type]++;
    });
  });

  const incomeByType: Record<BuildingType, number> = {
    house: buildingCounts.house * BUILDING_COIN_INCOME.house,
    company: buildingCounts.company * BUILDING_COIN_INCOME.company,
    powerPlant: buildingCounts.powerPlant * BUILDING_COIN_INCOME.powerPlant,
  };

  const totalIncome = Object.values(incomeByType).reduce((s, v) => s + v, 0);

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";

  // Simulated daily income history (last 7 days)
  const dailyHistory = [
    { day: "3/27", income: Math.max(0, totalIncome * 22) },
    { day: "3/28", income: Math.max(0, totalIncome * 23) },
    { day: "3/29", income: Math.max(0, totalIncome * 22) },
    { day: "3/30", income: Math.max(0, totalIncome * 24) },
    { day: "3/31", income: Math.max(0, totalIncome * 24) },
    { day: "4/1", income: Math.max(0, totalIncome * 24) },
    { day: "4/2", income: Math.max(0, totalIncome * 2) },
  ];

  const maxDaily = Math.max(...dailyHistory.map((d) => d.income), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
      {/* Income Breakdown */}
      <div className={`rounded-2xl border p-6 ${cardBg}`}>
        <h3 className={`text-sm font-bold mb-1 ${textPrimary}`}>建物別収入</h3>
        <p className={`text-xs mb-5 ${textSecondary}`}>時間あたりのコイン収入</p>
        <div className="space-y-4">
          {(Object.keys(BUILDING_LABELS) as BuildingType[]).map((type) => {
            const pct = totalIncome > 0 ? (incomeByType[type] / totalIncome) * 100 : 0;
            const c = BUILDING_COLORS[type];
            return (
              <div key={type}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${c.bg}`}>
                      <i className={`${BUILDING_ICONS[type]} text-sm ${c.text}`}></i>
                    </div>
                    <span className={`text-xs font-medium ${textPrimary}`}>{BUILDING_LABELS[type]}</span>
                    <span className={`text-xs ${textSecondary}`}>×{buildingCounts[type]}</span>
                  </div>
                  <span className={`text-sm font-bold ${c.text}`}>+{incomeByType[type]}</span>
                </div>
                <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${c.bar}`}
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
        <div className={`mt-5 pt-4 border-t flex items-center justify-between ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <span className={`text-xs ${textSecondary}`}>合計収入/h</span>
          <span className="text-amber-400 text-lg font-black">+{totalIncome} coins</span>
        </div>
      </div>

      {/* Daily Income Bar Chart */}
      <div className={`rounded-2xl border p-6 lg:col-span-2 ${cardBg}`}>
        <h3 className={`text-sm font-bold mb-1 ${textPrimary}`}>日別コイン収入（過去7日）</h3>
        <p className={`text-xs mb-5 ${textSecondary}`}>建物からの自動収入</p>
        <div className="flex items-end gap-3 h-36">
          {dailyHistory.map((d, i) => {
            const heightPct = (d.income / maxDaily) * 100;
            const isToday = i === dailyHistory.length - 1;
            return (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                <span className={`text-xs font-bold ${isToday ? "text-amber-400" : textSecondary}`}>
                  {d.income > 0 ? `+${d.income}` : "0"}
                </span>
                <div className="w-full flex items-end" style={{ height: "80px" }}>
                  <div
                    className={`w-full rounded-t-lg transition-all duration-700 ${
                      isToday ? "bg-amber-400" : isDark ? "bg-white/10" : "bg-gray-200"
                    }`}
                    style={{ height: `${Math.max(heightPct, 4)}%` }}
                  ></div>
                </div>
                <span className={`text-xs ${isToday ? "text-amber-400 font-bold" : textSecondary}`}>
                  {d.day}
                </span>
              </div>
            );
          })}
        </div>
        {/* Income projection */}
        <div className={`mt-5 pt-4 border-t grid grid-cols-3 gap-4 ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <div className="text-center">
            <p className={`text-xs ${textSecondary} mb-1`}>今日の収入</p>
            <p className="text-amber-400 font-bold text-sm">+{totalIncome * 2}</p>
          </div>
          <div className="text-center">
            <p className={`text-xs ${textSecondary} mb-1`}>今週の収入</p>
            <p className="text-emerald-400 font-bold text-sm">+{totalIncome * 24 * 7}</p>
          </div>
          <div className="text-center">
            <p className={`text-xs ${textSecondary} mb-1`}>今月の収入</p>
            <p className="text-sky-400 font-bold text-sm">+{totalIncome * 24 * 30}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
